/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Selection Store (Zustand)
   Tracks active selected nodes and edges.
   ═══════════════════════════════════════════════════════════════ */

import { create } from 'zustand';
import type { EdgeId, NodeId } from '../domain/types';

interface SelectionStore {
  selectedNodeIds: Set<NodeId>;
  selectedEdgeIds: Set<EdgeId>;

  selectNode: (nodeId: NodeId, additive?: boolean) => void;
  selectEdge: (edgeId: EdgeId, additive?: boolean) => void;
  setSelection: (nodeIds: NodeId[], edgeIds?: EdgeId[]) => void;
  clearSelection: () => void;
  isNodeSelected: (nodeId: NodeId) => boolean;
  isEdgeSelected: (edgeId: EdgeId) => boolean;
}

export const useSelectionStore = create<SelectionStore>((set, get) => ({
  selectedNodeIds: new Set<NodeId>(),
  selectedEdgeIds: new Set<EdgeId>(),

  selectNode: (nodeId: NodeId, additive = false) => {
    set((s) => {
      if (additive) {
        const next = new Set(s.selectedNodeIds);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return { selectedNodeIds: next };
      }
      return {
        selectedNodeIds: new Set([nodeId]),
        selectedEdgeIds: new Set(),
      };
    });
  },

  selectEdge: (edgeId: EdgeId, additive = false) => {
    set((s) => {
      if (additive) {
        const next = new Set(s.selectedEdgeIds);
        if (next.has(edgeId)) {
          next.delete(edgeId);
        } else {
          next.add(edgeId);
        }
        return { selectedEdgeIds: next };
      }
      return {
        selectedNodeIds: new Set(),
        selectedEdgeIds: new Set([edgeId]),
      };
    });
  },

  setSelection: (nodeIds: NodeId[], edgeIds: EdgeId[] = []) => {
    set({
      selectedNodeIds: new Set(nodeIds),
      selectedEdgeIds: new Set(edgeIds),
    });
  },

  clearSelection: () => {
    set({
      selectedNodeIds: new Set(),
      selectedEdgeIds: new Set(),
    });
  },

  isNodeSelected: (nodeId: NodeId) => get().selectedNodeIds.has(nodeId),
  isEdgeSelected: (edgeId: EdgeId) => get().selectedEdgeIds.has(edgeId),
}));
